/**
 * Bake-and-strip pipeline for E-1 (and used by E-3 zip export).
 *
 * Inputs: a complete HTML artifact + the user's text edits + tweak overrides.
 * Output: a self-contained HTML document with:
 *   - All edits applied to the matching elements' textContent.
 *   - Tweak overrides injected as a final `<style>` block.
 *   - The runtime injection (`<script id="cdc-runtime">`) and the
 *     `cdc-tweaks` declaration stripped.
 *
 * This runs in the browser via DOMParser, which keeps it dependency-free.
 */

export interface BakeInput {
  html: string;
  edits: Array<{ selector: string; newText: string }>;
  tweaks: Array<{ variable: string; value: string }>;
}

const RUNTIME_TAG_RE = /<script\s+id=["']cdc-runtime["'][^>]*>[\s\S]*?<\/script>/i;
const RUNTIME_TAG_MARKER_RE =
  /<script\s+id=["']cdc-runtime["'][\s\S]*?<\/script>/i;
const TWEAKS_TAG_RE =
  /<script[^>]+id=["']cdc-tweaks["'][^>]*>[\s\S]*?<\/script>/i;
const CSP_META_RE = /<meta\b[^>]*data-cdc-csp-injected[^>]*>/gi;

export function bakeHtml({ html, edits, tweaks }: BakeInput): string {
  // Parse, then mutate via DOM.
  const doc = new DOMParser().parseFromString(html, 'text/html');

  for (const edit of edits) {
    let target: Element | null = null;
    try {
      target = doc.querySelector(edit.selector);
    } catch {
      target = null;
    }
    if (target) {
      target.textContent = edit.newText;
    }
  }

  if (tweaks.length > 0) {
    const style = doc.createElement('style');
    style.id = 'cdc-final-tweaks';
    const lines = [':root {'];
    for (const t of tweaks) {
      lines.push(`  ${t.variable}: ${t.value};`);
    }
    lines.push('}');
    style.textContent = lines.join('\n');
    (doc.head ?? doc.body)?.appendChild(style);
  }

  // Strip the runtime + cdc-tweaks declaration block.
  doc.getElementById('cdc-runtime')?.remove();
  doc.getElementById('cdc-tweaks')?.remove();
  // Strip the host-injected CSP meta — exported HTML shouldn't carry the
  // dev-only iframe CSP; let the user's chosen CSP (if any) win.
  for (const meta of Array.from(doc.querySelectorAll('meta[data-cdc-csp-injected]'))) {
    meta.remove();
  }

  // DOMParser drops the doctype off the serializer, so prepend it manually.
  const doctype = doc.doctype
    ? `<!DOCTYPE ${doc.doctype.name}>`
    : '<!DOCTYPE html>';
  return `${doctype}\n${doc.documentElement.outerHTML}`;
}

/** Trigger a Blob-based download in the browser. Browser-only. */
export function downloadAsFile(filename: string, content: string, mime = 'text/html'): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revocation so Safari has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Internal: lets E-2 PDF export and tests detect that bake stripped tags cleanly. */
export function _hasArtifactPlumbing(html: string): boolean {
  return (
    RUNTIME_TAG_RE.test(html) ||
    RUNTIME_TAG_MARKER_RE.test(html) ||
    TWEAKS_TAG_RE.test(html) ||
    CSP_META_RE.test(html)
  );
}
